import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import {
  Calendar, Users, GraduationCap, ChevronRight, BookOpen,
  CheckCircle2, AlertTriangle, Info, Clock, MapPin,
} from 'lucide-react';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';
import { TurmaDetalheSheet } from './TurmaDetalheSheet';
import { MinhasTurmasDesktop } from './MinhasTurmasDesktop';
import {
  buildGradeTurma,
  TURNO_LABEL,
  useMinhasTurmasProfessor,
} from './useMinhasTurmasProfessor';

function useIsMobile(bp = 768) {
  const [m, setM] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < bp : false,
  );
  useEffect(() => {
    const fn = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return m;
}

export function MinhasTurmasPage() {
  const escolaId = useAuthStore((s) => s.escolaId);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    diaSemana,
    horariosPorDia,
    isLoading,
    turmasComContagem,
    pendentesChamada,
    contagemAlunos,
  } = useMinhasTurmasProfessor();

  const [sheetId, setSheetId] = useState<string | null>(null);
  const [initialTurmaId, setInitialTurmaId] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as { turmaId?: string } | null;
    if (state?.turmaId) {
      if (isMobile) setSheetId(state.turmaId);
      else setInitialTurmaId(state.turmaId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, isMobile]);

  const mt = turmasComContagem.find((t) => t.id === sheetId);
  const grade = sheetId ? buildGradeTurma(sheetId, horariosPorDia) : [];

  const [alunosTurma, setAlunosTurma] = useState<{ id: string; nome: string }[]>([]);
  const [loadingAlunos, setLoadingAlunos] = useState(false);

  useEffect(() => {
    if (!isMobile || !sheetId || !escolaId) {
      setAlunosTurma([]);
      return;
    }
    let cancelled = false;
    setLoadingAlunos(true);
    api
      .get<{ alunos?: { id: string; nome: string }[] }>(
        `/escolas/${escolaId}/chamadas/turma/${sheetId}/alunos`,
      )
      .then(({ data }) => {
        if (!cancelled) setAlunosTurma(data.alunos ?? []);
      })
      .catch(() => {
        if (!cancelled) setAlunosTurma([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAlunos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sheetId, escolaId, isMobile]);

  if (!isMobile) {
    return (
      <div className="tur-desk-host">
        <MinhasTurmasDesktop
          turmasComContagem={turmasComContagem}
          isLoading={isLoading}
          pendentesChamada={pendentesChamada}
          diaSemana={diaSemana}
          horariosPorDia={horariosPorDia}
          contagemAlunos={contagemAlunos}
          initialTurmaId={initialTurmaId}
        />
      </div>
    );
  }

  const lista = (
    <>
      <div className="turmas-info-banner">
        <Info size={14} aria-hidden />
        <span>
          Turmas extraídas da <strong>sua grade de horários</strong>. Toque em uma turma
          para ver alunos, matérias e a grade da semana. Contagem de alunos vem da turma
          vinculada à sua escola.
        </span>
      </div>

      {pendentesChamada > 0 && (
        <div className="turmas-alert-banner">
          <AlertTriangle size={14} />
          <span>
            {pendentesChamada} turma{pendentesChamada > 1 ? 's' : ''} com chamada pendente hoje
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="turmas-empty">Carregando turmas...</div>
      ) : turmasComContagem.length === 0 ? (
        <div className="turmas-empty">
          <Calendar size={32} aria-hidden />
          <p>Nenhuma turma na sua grade</p>
          <span>Peça à secretaria para vincular suas aulas no horário.</span>
        </div>
      ) : (
        <div className="turmas-list">
          {turmasComContagem.map((t) => {
            const pendente = t.aulasHoje > 0 && !t.chamadaHojeFeita;
            const materiasStr = [...t.materias].join(', ');
            return (
              <button
                key={t.id}
                type="button"
                className={`turmas-card ${pendente ? 'turmas-card--pend' : ''}`}
                onClick={() => setSheetId(t.id)}
              >
                <div className="turmas-card-icon" aria-hidden>
                  <GraduationCap size={20} />
                </div>
                <div className="turmas-card-body">
                  <div className="turmas-card-title-row">
                    <span className="turmas-card-title">{t.nome}</span>
                    {t.turno && (
                      <span className="turmas-card-turno">
                        {TURNO_LABEL[t.turno] || t.turno}
                      </span>
                    )}
                  </div>
                  <div className="turmas-card-meta">
                    <span title="Alunos ativos na turma">
                      <Users size={11} />
                      <strong>{contagemAlunos[t.id] !== undefined ? t.totalAlunos : '…'}</strong>{' '}
                      aluno{t.totalAlunos !== 1 ? 's' : ''}
                    </span>
                    <span title="Matérias na sua grade">
                      <BookOpen size={11} />
                      {t.materias.size} matéria{t.materias.size !== 1 ? 's' : ''}
                    </span>
                    {t.aulasHoje > 0 && (
                      <span title="Aulas hoje">
                        <Clock size={11} />
                        {t.aulasHoje} hoje
                      </span>
                    )}
                    {t.sala && (
                      <span title="Sala">
                        <MapPin size={11} />
                        {t.sala}
                      </span>
                    )}
                  </div>
                  {t.materias.size > 0 && (
                    <div className="turmas-card-materias-pills" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {[...t.materias].slice(0, 3).map((m) => (
                        <span key={m} style={{ fontSize: '.68rem', background: '#f1f5f9', color: '#334155', padding: '2px 7px', borderRadius: 5, fontWeight: 600 }}>
                          {m}
                        </span>
                      ))}
                      {t.materias.size > 3 && (
                        <span style={{ fontSize: '.68rem', background: '#e2e8f0', color: '#475569', padding: '2px 7px', borderRadius: 5, fontWeight: 700 }}>
                          +{t.materias.size - 3} mais
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="turmas-card-aside">
                  {t.aulasHoje > 0 &&
                    (t.chamadaHojeFeita ? (
                      <span className="turmas-badge turmas-badge--ok">
                        <CheckCircle2 size={11} /> Chamada OK
                      </span>
                    ) : (
                      <span className="turmas-badge turmas-badge--pend">
                        <AlertTriangle size={11} /> Chamada pend.
                      </span>
                    ))}
                  <ChevronRight size={16} className="turmas-card-chevron" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <>
      <MobilePageWrapper
        title="Minhas Turmas"
        icon={GraduationCap}
        iconColor="#93c5fd"
        hideBackButton
        badge={turmasComContagem.length}
      >
        {lista}
      </MobilePageWrapper>
      <TurmaDetalheSheet
        turma={mt ?? null}
        grade={grade}
        diaSemanaHoje={diaSemana}
        alunos={alunosTurma}
        loadingAlunos={loadingAlunos}
        onClose={() => setSheetId(null)}
      />
    </>
  );
}
